import { ClientTable } from "../../../components/ui/ClientTable";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus, Save } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, LoadingState, ui } from "../../../components/ui";
import { cn } from "../../../lib/utils";
import {
  fetchCaseIndexQuestions,
  fetchCaseIndexReviews,
  getLead,
  saveCaseIndexReview,
} from "../../../services/leadManagementService";
import { formatDay, isLockedLead } from "../leadForm";
import { LeadPageHeader, LeadTabs, openLeadTab } from "./LeadDetailPage";

const LEADS_PATH = "/crm/lead-management/leads";

const K = "leadManagement.caseIndex.";

/* ----------------------------------------------------------- scoring */

/** A question scores its weight only when the answer matches its polarity. */
const scoreOf = (question, answer) => (answer === question.scoresBasedOnYn ? question.questionWeight : 0);

/** Position bands, upper-inclusive: 0–30, 30–50, 50–70, above 70. */
const bandOf = (index) => (index <= 30 ? "Very_Weak" : index <= 50 ? "Weak" : index <= 70 ? "Moderate" : "Strong");

const round2 = (value) => Math.round(value * 100) / 100;

/** The questions grouped by category, each with its maximum (the sum of its weights). */
function groupByCategory(questions) {
  const groups = [];
  questions.forEach((question) => {
    let group = groups.find((entry) => entry.number === question.categoryNumber);
    if (!group) {
      group = { number: question.categoryNumber, name: question.categoryName, questions: [], max: 0 };
      groups.push(group);
    }
    group.questions.push(question);
    group.max += question.questionWeight;
  });
  return groups;
}

/** One review: each category's score and percentage, the overall index and its band. */
function scoreReview(categories, answers) {
  const byCategory = categories.map((category) => {
    const score = category.questions.reduce((sum, question) => sum + scoreOf(question, answers[question.questionId]), 0);
    return { score, percent: (score / category.max) * 100 };
  });
  const overall = byCategory.reduce((sum, category) => sum + category.score, 0);
  return { byCategory, overall, band: bandOf(overall) };
}

const columnFromApi = (review) => ({
  reviewId: review.reviewId,
  reviewDate: review.reviewDate,
  isLocked: review.isLocked,
  createdName: review.createdName,
  answers: Object.fromEntries(review.reviewAnswers.map((row) => [row.questionId, row.answer])),
});

/** A new review is dated today (local yyyy-mm-dd); the date is not editable. */
const draftColumn = () => ({
  reviewId: null,
  reviewDate: new Date().toLocaleDateString("en-CA"),
  isLocked: false,
  createdName: null,
  answers: {},
});

/* -------------------------------------------------------------- parts */

const BAND_TONE = { Very_Weak: "rejected", Weak: "warning", Moderate: "scheduled", Strong: "published" };

function BandBadge({ band }) {
  const { t } = useTranslation();
  const tone = BAND_TONE[band];
  return (
    <span
      className="inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold"
      style={{ backgroundColor: `var(--status-${tone}-bg)`, color: `var(--status-${tone}-fg)` }}
    >
      {t(`${K}band${band}`)}
    </span>
  );
}

function AnswerToggle({ value, onChange }) {
  const { t } = useTranslation();
  const choice = (answer, label) => (
    <button
      type="button"
      onClick={() => onChange(value === answer ? null : answer)}
      className={cn(
        "h-8 flex-1 text-[12px] font-semibold transition",
        value === answer ? "bg-primary text-white" : "bg-white per-file-hint hover:text-primary"
      )}
    >
      {label}
    </button>
  );
  return (
    <div className="flex w-24 divide-x divide-(--color-text-border) overflow-hidden rounded-lg border border-(--color-text-border)">
      {choice(true, t("common.yes"))}
      {choice(false, t("common.no"))}
    </div>
  );
}

const cell = "px-3 py-2.5 align-middle";

/* ----------------------------------------------------------- content */

/**
 * Case Index (PRD Screen 4). Scoring happens here: the backend stores each
 * review's answers, overall index and band as sent. The latest review is the
 * only editable one; adding a review locks every earlier one.
 */
function CaseIndexContent({ leadId, locked }) {
  const { t } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const load = useCallback(() => {
    setLoading(true);
    return Promise.all([fetchCaseIndexQuestions(), fetchCaseIndexReviews(leadId)])
      .then(([questionRows, reviews]) => {
        setQuestions(questionRows);
        // A Case Index starts with one review, the Opening.
        setColumns(reviews.length ? reviews.map(columnFromApi) : [draftColumn()]);
      })
      .catch((error) => setMessage({ type: "error", text: error.message }))
      .finally(() => setLoading(false));
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <ContentCard>
        <LoadingState message={t(`${K}loading`)} />
      </ContentCard>
    );
  }

  // Nothing loaded: the reason is the only thing to show.
  if (!columns.length) {
    return (
      <ContentCard>
        <p className="p-5 text-[12px] font-semibold text-rose-600">{message.text}</p>
      </ContentCard>
    );
  }

  const categories = groupByCategory(questions);
  const scored = columns.map((column) => scoreReview(categories, column.answers));
  const last = columns.length - 1;
  const editable = !locked && !columns[last].isLocked;
  const reviewName = (index) => (index === 0 ? t(`${K}opening`) : t(`${K}reviewN`, { number: index + 1 }));
  // The questions list the newest review beside the question, back to the Opening.
  const newestFirst = columns.map((column, index) => ({ column, index })).reverse();

  // Section B averages every saved review, including any that score zero.
  const saved = scored.filter((_, index) => columns[index].reviewId);
  const average = (pick) => saved.reduce((sum, review) => sum + pick(review), 0) / saved.length;

  const updateLatest = (patch) => {
    setMessage({ type: "", text: "" });
    setColumns((current) => current.map((column, index) => (index === last ? { ...column, ...patch(column) } : column)));
  };

  const save = async () => {
    const column = columns[last];
    setBusy(true);
    try {
      await saveCaseIndexReview(leadId, {
        reviewId: column.reviewId ?? undefined,
        reviewDate: column.reviewDate,
        overallCaseIndex: round2(scored[last].overall),
        reviewStatus: scored[last].band,
        reviewAnswers: questions.map((question) => ({
          questionId: question.questionId,
          answer: column.answers[question.questionId] ?? null,
          scores: scoreOf(question, column.answers[question.questionId]),
        })),
      });
      await load();
      setMessage({ type: "success", text: t(`${K}saved`) });
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setBusy(false);
    }
  };

  // The latest review is answered with a toggle; every other cell reads its saved answer.
  const answerCell = (column, index, question) => {
    const answer = column.answers[question.questionId];
    return editable && index === last ? (
      <AnswerToggle
        value={answer}
        onChange={(value) => updateLatest((current) => ({ answers: { ...current.answers, [question.questionId]: value } }))}
      />
    ) : (
      <span className="text-[12px] font-semibold text-foreground">
        {answer === true ? t("common.yes") : answer === false ? t("common.no") : "—"}
      </span>
    );
  };

  const categoryScore = (review, categoryIndex) =>
    `${round2(review.byCategory[categoryIndex].percent)}%`;

  const scoresOn = (question) => t(`${K}scoresOn`, { answer: question.scoresBasedOnYn ? t("common.yes") : t("common.no") });

  const reviewLabel = (column, index) => (
    <>
      {reviewName(index)}
      {column.reviewId ? null : <span className="ml-1 text-[11px] font-normal normal-case per-file-hint">{t(`${K}unsaved`)}</span>}
    </>
  );

  return (
    <div className="space-y-5">
      {/* Section B — Average Index Components, averaged over every saved review. */}
      <ContentCard title={t(`${K}sectionB`)} bodyClassName="form-section">
        {saved.length ? (
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {categories.map((category, index) => (
              <div key={category.number} className="rounded-xl border border-(--color-text-border) px-4 py-3">
                <p className="text-[12px] per-file-hint">{category.name}</p>
                <p className="mt-1 text-xl font-bold text-foreground">
                  {round2(average((review) => review.byCategory[index].percent))}%
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-[12px] per-file-hint">{t(`${K}noSaved`)}</p>
        )}
      </ContentCard>

      {/* Review summary: each review's date, creator, category scores and overall index. */}
      <ContentCard title={t(`${K}reviewSummary`)} bodyClassName="p-0">
        <div className="hidden overflow-x-auto md:block">
          <ClientTable className="w-full text-sm" style={{ minWidth: `${440 + categories.length * 150}px` }}>
            <thead>
              <tr className={ui.tableHead}>
                <th className={cn(cell, "font-semibold")}>{t(`${K}review`)}</th>
                <th className={cn(cell, "font-semibold")}>{t(`${K}reviewDate`)}</th>
                <th className={cn(cell, "font-semibold")}>{t("formActions.createdBy")}</th>
                {categories.map((category) => (
                  <th key={category.number} className={cn(cell, "font-semibold")}>
                    <span className="block">{category.name}</span>
                    <span className="block normal-case font-bold text-foreground">{t(`${K}maximum`, { max: category.max })}</span>
                  </th>
                ))}
                <th className={cn(cell, "font-semibold")}>{t(`${K}overall`)}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-text-border)/35">
              {columns.map((column, index) => (
                <tr key={column.reviewId ?? "draft"}>
                  <td className={cn(cell, "font-semibold text-foreground")}>{reviewLabel(column, index)}</td>
                  <td className={cn(cell, "per-file-hint")}>{formatDay(column.reviewDate)}</td>
                  <td className={cn(cell, "text-foreground")}>{column.createdName || "—"}</td>
                  {categories.map((category, categoryIndex) => (
                    <td key={category.number} className={cn(cell, "text-[12px] font-bold text-foreground")}>
                      {categoryScore(scored[index], categoryIndex)}
                    </td>
                  ))}
                  <td className={cell}>
                    <span className="mr-2 font-bold text-foreground">{round2(scored[index].overall)}</span>
                    <BandBadge band={scored[index].band} />
                  </td>
                </tr>
              ))}
            </tbody>
          </ClientTable>
        </div>

        <div className="divide-y divide-(--color-text-border)/35 md:hidden">
          {columns.map((column, index) => (
            <div key={column.reviewId ?? "draft"} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground">{reviewLabel(column, index)}</p>
                  <p className="text-[11px] per-file-hint">
                    {formatDay(column.reviewDate)} · {column.createdName || "—"}
                  </p>
                </div>
                <span className="shrink-0">
                  <span className="mr-2 font-bold text-foreground">{round2(scored[index].overall)}</span>
                  <BandBadge band={scored[index].band} />
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-1.5">
                {categories.map((category, categoryIndex) => (
                  <div key={category.number} className="rounded-lg bg-workspace/40 px-2.5 py-1.5">
                    <p className="truncate text-[11px] per-file-hint">
                      {category.name} ({t(`${K}maximum`, { max: category.max })})
                    </p>
                    <p className="text-[12px] font-bold text-foreground">{categoryScore(scored[index], categoryIndex)}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ContentCard>

      {/* The questions, answered per review. */}
      <ContentCard
        title={t(`${K}questions`)}
        bodyClassName="p-0"
        action={
          !locked && columns[last].reviewId ? (
            <button type="button" onClick={() => setColumns((current) => [...current, draftColumn()])} className={ui.filterBtn}>
              <Plus className="h-4 w-4" />
              {t(`${K}addReview`)}
            </button>
          ) : null
        }
      >
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full text-sm" style={{ minWidth: `${560 + columns.length * 140}px` }}>
            <thead>
              <tr className={ui.tableHead}>
                <th className={cn(cell, "w-12 font-semibold")}>{t(`${K}ref`)}</th>
                <th className={cn(cell, "font-semibold")}>{t(`${K}question`)}</th>
                <th className={cn(cell, "w-20 text-center font-semibold")}>{t(`${K}weight`)}</th>
                {newestFirst.map(({ column, index }) => (
                  <th key={column.reviewId ?? "draft"} className={cn(cell, "w-36 font-semibold")}>
                    {reviewLabel(column, index)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-(--color-text-border)/35">
              {categories.map((category) => [
                <tr key={`category-${category.number}`} className="bg-workspace/30">
                  <td colSpan={3 + columns.length} className={cn(cell, "font-bold text-foreground")}>
                    {category.number}. {category.name}
                  </td>
                </tr>,
                ...category.questions.map((question) => (
                  <tr key={question.questionId}>
                    <td className={cn(cell, "font-mono text-[12px] font-semibold text-foreground")}>{question.questionId}</td>
                    <td className={cn(cell, "text-foreground")}>{question.questionText}</td>
                    <td className={cn(cell, "text-center")}>
                      <span className="block font-bold text-foreground">{question.questionWeight}</span>
                      <span className="block text-[11px] per-file-hint">{scoresOn(question)}</span>
                    </td>
                    {newestFirst.map(({ column, index }) => (
                      <td key={column.reviewId ?? "draft"} className={cell}>
                        {answerCell(column, index, question)}
                      </td>
                    ))}
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>

        <div className="md:hidden">
          {categories.map((category) => (
            <div key={category.number}>
              <p className="bg-workspace/30 px-4 py-3 text-sm font-bold text-foreground">
                {category.number}. {category.name}
              </p>
              <div className="divide-y divide-(--color-text-border)/35">
                {category.questions.map((question) => (
                  <div key={question.questionId} className="px-4 py-3">
                    <div className="flex items-start gap-3">
                      <p className="flex min-w-0 flex-1 gap-2 text-sm text-foreground">
                        <span className="shrink-0 font-mono text-[12px] font-semibold leading-5">{question.questionId}</span>
                        <span>{question.questionText}</span>
                      </p>
                      <p className="shrink-0 text-center">
                        <span className="block text-sm font-bold text-foreground">{question.questionWeight}</span>
                        <span className="block text-[11px] per-file-hint">{scoresOn(question)}</span>
                      </p>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2">
                      {newestFirst.map(({ column, index }) => (
                        <div key={column.reviewId ?? "draft"} className="space-y-1">
                          <span className="block text-[11px] per-file-hint">{reviewLabel(column, index)}</span>
                          {answerCell(column, index, question)}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </ContentCard>

      {message.text ? (
        <p className={cn("text-[12px] font-semibold", message.type === "error" ? "text-rose-600" : "text-emerald-700")}>
          {message.text}
        </p>
      ) : null}

      {editable ? (
        <div className="flex justify-end">
          <button type="button" onClick={save} disabled={busy} className={cn(ui.primaryBtn, "px-6")}>
            <Save className="h-4 w-4" />
            {busy ? t("leadManagement.saving") : t(`${K}save`, { review: reviewName(last) })}
          </button>
        </div>
      ) : null}
    </div>
  );
}

/* -------------------------------------------------------------- page */

/** Case Index has its own URL, so a reload stays on it. The other tabs live on the detail page. */
export function CaseIndexPage() {
  const { t } = useTranslation();
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    getLead(leadId)
      .then(setLead)
      .catch((error) => setLoadError(error.message));
  }, [leadId]);

  const openTab = (tab) => {
    if (tab !== "caseIndex") openLeadTab(navigate, leadId, tab, lead);
  };

  return (
    <AppLayout className="space-y-5">
      {lead ? (
        <LeadPageHeader lead={lead} />
      ) : null}
      {loadError ? <p className="text-[12px] font-semibold text-rose-600">{loadError}</p> : null}

      <LeadTabs active="caseIndex" onChange={openTab} />

      {lead ? (
        <CaseIndexContent leadId={leadId} locked={isLockedLead(lead)} />
      ) : (
        <ContentCard>
          <LoadingState message={t(`${K}loading`)} />
        </ContentCard>
      )}
    </AppLayout>
  );
}

export default CaseIndexPage;
